import { SetStateAction } from 'react';

/**
 * 콘텐츠 플레이어 창에 전달하는 데이터. type별로 contents 형태가 다른 판별 유니온.
 * - contentsView: 수업자료 콘텐츠 표시
 * - todoView: 과제(젭스플레이어) 콘텐츠 표시
 * - learningView: 교사대학 콘텐츠 표시
 */
export type ContentsViewData =
  | {
      type: 'contentsView';
      contents: { classId?: string; contentId: string; isLight?: boolean };
    }
  | { type: 'learningView'; contents: { learningId: string; lessonId: string } }
  | { type: 'todoView'; contents: { url: string; accessToken?: string } };

/**
 * 콘텐츠 플레이어 창(팝업)을 여는 저수준 프리미티브
 * @description
 * - '/contents' 팝업을 열어 data를 postMessage로 전달합니다.
 * - 특정 type에 치우치지 않는 범용 함수이며, type별 진입점은 openTodoView / openContentsView / openLearningView를 사용하세요.
 * - 새 창이 열리면 기존 창은 닫힙니다.
 * @param data 콘텐츠 데이터
 * @param setWindow(optional) 창 상태를 관리하는 React setState 함수, 있으면 새 창이 열리면 기존 창을 닫고 새 창을 열고 그 참조를 저장함
 * @param onClose(optional) 창을 닫고 실행할 함수
 * @returns
 */
export const openContentsWindow = (
  data: ContentsViewData,
  setWindow?: (value: SetStateAction<Window>) => void, //창 상태를 관리하는 React setState 함수, 있으면 새 창이 열리면 기존 창을 닫고 새 창을 열고, 그 참조를 저장함
  onClose?: () => void,
) => {
  const openWindow = () => {
    // 새 창 열기
    const newWindow = window.open(
      '/contents',
      'ContentsView',
      'location=no, menubar=no,',
    );

    // 새 창이 열리면 콘텐츠 데이터를 전달
    if (newWindow) {
      newWindow.name = 'popup';
      newWindow.onload = () => {
        newWindow.postMessage(data, window.location.origin);
      };
    }

    if (onClose) {
      const timer = window.setInterval(() => {
        if (newWindow.closed) {
          window.clearInterval(timer);

          onClose();
        }
      }, 500);

      return newWindow;
    }
  };

  // setWindow가 있으면, 새 창이 열리면 기존 창을 닫고 새 창을 열고, 그 참조를 저장함
  if (setWindow) {
    setWindow((prev) => {
      if (prev !== undefined) {
        prev.close();
      }
      return openWindow();
    });
  } else {
    // setWindow가 없으면, 그냥 새 창을 연다
    openWindow();
  }
};

/**
 * 젭스플레이어 링크를 실제 오픈할 URL로 변환합니다.
 * @description 로컬 개발 서버(NODE_ENV=development)에서만 젭스플레이어 링크를 로컬 주소로 파싱합니다.
 * @param link 젭스플레이어 콘텐츠 링크(full URL)
 * @returns 오픈에 사용할 URL
 */
export const resolveJebsplayerUrl = (link: string): string =>
  process.env.NODE_ENV === 'development'
    ? `${process.env.REACT_APP_JEBS_PLAYER_URL}${new URL(link).pathname}`
    : link;

/**
 * todoView(학생 과제: 젭스플레이어) 콘텐츠 창을 오픈합니다.
 * @description accessToken 주입 등 todoView 고유 처리를 담당하고, 창 관리는 openContentsWindow에 위임합니다.
 * @param contents 콘텐츠 데이터(url 필수). accessToken은 항상 localStorage 값으로 채웁니다.
 * @param setWindow(optional) 창 상태를 관리하는 React setState 함수(있으면 기존 창을 닫고 새 창을 엶)
 * @param onClose(optional) 창을 닫고 실행할 함수
 */
export const openTodoView = (
  contents: { url: string },
  setWindow?: (value: SetStateAction<Window>) => void,
  onClose?: () => void,
) =>
  openContentsWindow(
    {
      type: 'todoView',
      contents: {
        ...contents,
        accessToken: localStorage.getItem('accessToken') ?? '',
      },
    },
    setWindow,
    onClose,
  );

/**
 * contentsView(수업 콘텐츠) 창을 오픈합니다.
 * @description classId/contentId 기반 콘텐츠 payload 조립을 담당하고, 창 관리는 openContentsWindow에 위임합니다.
 * @param classId 클래스 id
 * @param contentId 콘텐츠 id
 * @param options isLight 등 부가 옵션
 * @param setWindow 창 상태를 관리하는 React setState 함수
 */
export const openContentsView = (
  classId: string | undefined,
  contentId: string,
  options?: { isLight?: boolean },
  setWindow?: (value: SetStateAction<Window>) => void,
) =>
  openContentsWindow(
    {
      type: 'contentsView',
      contents: { classId, contentId, isLight: options?.isLight },
    },
    setWindow,
  );

/**
 * learningView(교사대학) 창을 오픈합니다.
 * @description learningId/lessonId 기반 콘텐츠 payload 조립을 담당하고, 창 관리는 openContentsWindow에 위임합니다.
 * @param learningId 러닝 id
 * @param lessonId 레슨 id
 * @param setWindow 창 상태를 관리하는 React setState 함수
 */
export const openLearningView = (
  learningId: string,
  lessonId: string,
  setWindow?: (value: SetStateAction<Window>) => void,
) =>
  openContentsWindow(
    { type: 'learningView', contents: { learningId, lessonId } },
    setWindow,
  );
